import { computed, nextTick, reactive, type ComputedRef, type Ref, type UnwrapNestedRefs } from 'vue';
import { syncGeoItemWithParent } from 'src/lib/geoItemHelper';
import { applyMapPinPosition, getMgiDistance } from 'src/lib/moviePlayerHelper';
import type { GeoItemMeta, GeoItemsMeta, GIComment, GIMovieGeoIndex } from 'src/models/geoItem';
import type { IExtremeMapRef, Location, MapElemInfo } from 'src/models';
import type EMPinLayerManager from 'src/lib/extreme_map/EMPinLayerManager';
import type {
  BothLeftRight,
  LeftRight,
  MoviePlayerCompareModeState,
} from 'src/models/moviePlayer';

interface Deps {
  refExtremeMap: Ref<IExtremeMapRef | undefined>;
  parentGeoItemsMeta: GeoItemsMeta;
  seekMovie: (lr: LeftRight, offsetMsec: number) => Promise<void>;
  pauseUI: (lr: BothLeftRight) => void;
  playUI: (lr: BothLeftRight) => void;
  getUIPlayState: (lr: LeftRight) => 'playing' | 'paused';
  getScreenshotBlob: (lr: LeftRight, vid: string) => Promise<Blob | string | null | undefined>;
  isCurrentMovieListBothReady: () => boolean;
}

interface MoviePlayerCompareModeMapLinkageContext {
  mapElemIconPath: string;
  visibleGeoItemLayers: ComputedRef<GeoItemMeta[]>;
  prepareMap: () => Promise<void>;
  updateMap: () => Promise<void>;
  updateMapPinPosition: (lr: LeftRight, location: { lat: string; lon: string }) => void;
  alignLocationToLeftMovie: () => Promise<void>;
  alignLocationToRightMovie: () => Promise<void>;
  seekByMapClick: (clickedLocation: Location) => Promise<void>;
  onMapItemClicked: () => Promise<void>;
  onMapAllDeselected: () => Promise<void>;
  startCreateMapElem: (lr: LeftRight, vid: string) => Promise<void>;
  createMapElem: (lr: LeftRight, obj: MapElemInfo<GIComment>) => Promise<void>;
  clearMapElemInfo: (lr: LeftRight) => void;
}

// 現在再生位置を示す左右の地図ピンのレイヤー内IDプレフィックス
// （'left' / 'right' を後置する）
const MAP_PIN_ID_PREFIX = 'map-pin1-';
// コメント新規作成時に地図上へ表示するアイコン画像パス。
// 実行中に変化しない値のためモジュール定数として定義する。
const MAP_ELEM_ICON_PATH = '/static/img/comment_icon_01.png';

// CompareMode用の地図連携。左右2ストリームの走行地点合わせ(alignLocationTo*)を含むため
// 単一ストリーム版のuseMoviePlayerMapLinkageとは別のcomposableとして持つ。
export function useMoviePlayerCompareModeMapLinkage(
  state: UnwrapNestedRefs<MoviePlayerCompareModeState>,
  deps: Deps,
): UnwrapNestedRefs<MoviePlayerCompareModeMapLinkageContext> {
  const mapElemIconPath = MAP_ELEM_ICON_PATH;
  const visibleGeoItemLayers = computed(() => {
    return [deps.parentGeoItemsMeta.map['comment']];
  });
  const prepareMap = async() => {
    const metaItem = state.geoItemsMeta.map.pin;
    const point1 = { id: `${MAP_PIN_ID_PREFIX}left`, location: { lat: 0, lon: 0 }, text: '左側' };
    const point2 = { id: `${MAP_PIN_ID_PREFIX}right`, location: { lat: 0, lon: 0 }, text: '右側', showWeak: true };
    if (deps.refExtremeMap.value) {
      deps.refExtremeMap.value.showDataLayer(metaItem, [point1, point2]);
    }
  };
  const updateMap = async() => {
    if (!deps.isCurrentMovieListBothReady() ||
      !state.left.movieList.listSelection.currentMovieList || !state.right.movieList.listSelection.currentMovieList
    ) {
      return;
    }
    const movieLists = [
      state.left.movieList.listSelection.currentMovieList,
      state.right.movieList.listSelection.currentMovieList,
    ];
    if (!deps.refExtremeMap.value) return;

    deps.refExtremeMap.value.refreshMovieLayer(movieLists);
    deps.refExtremeMap.value.fitToMovieListsExtent(movieLists);
  };
  const updateMapPinPosition = (lr: LeftRight, { lat, lon }: { lat: string; lon: string }) => {
    const layerMgr = state.geoItemsMeta.map.pin.layerManager as EMPinLayerManager;
    const mapPinId = `${MAP_PIN_ID_PREFIX}${lr}`;
    applyMapPinPosition(layerMgr, mapPinId, lat, lon);
  };
  const findClosestMgi = (refMgi: GIMovieGeoIndex, mgis: GIMovieGeoIndex[]) => {
    // 向島(上) -> 江戸橋JCT -> 都環(外) などのように、曲がりくねっている
    // ところの場合は一旦遠ざかってからまた近づくことがありえるので、厳密には
    // 全部見ていかないとわからない.
    // ただし、同一路線名#方向であることは画面的に保証されているので、
    // 十分に近づいたところからdistanceが増えた場合は、通り過ぎたと判断しても
    // よいだろう.
    let minDistance = 999999; // m
    let minDistanceMgi = mgis[0];
    // mgiが10秒間隔として、60km/h走行で200m弱間隔なので、その半分くらい
    const closeEnoughThres = 90; // m
    const distanceMap: Record<number, number> = {};
    for (const [idx, mgi] of mgis.entries()) {
      const distance = getMgiDistance(refMgi, mgi);
      distanceMap[idx] = distance;
      if (minDistance <= closeEnoughThres && distance > minDistance) {
        // 十分に近づいたところからまた遠くなったら、確定でよいだろう
        return minDistanceMgi;
      }
      if (distance < minDistance) {
        minDistance = distance;
        minDistanceMgi = mgi;
      }
    }
    // 十分に近づかなかった場合
    const { idx } = Object.entries(distanceMap).reduce((acc, [idx, distance]) => {
      if (distance < acc.min) {
        acc.min = distance;
        acc.idx = parseInt(idx);
        return acc;
      }
      return acc;
    }, { min: 999999, idx: -1 });
    return mgis[idx];
  };
  // isSeekingはplaybackが所有するフィールド。deps.seekMovie内部でtrue/falseが管理されるため、
  // ここでは直接書き換えない（かつては本関数でも冗長に直接書き換えていた）。
  const seekByMgi = async(lr: LeftRight, mgi?: GIMovieGeoIndex | null) => {
    if (!mgi || !mgi.ts) return;

    // movieListの開始と指定された時刻の差に、seek.minのずらし分を考慮
    // 比較モードの場合はルート検索がないので、これでOK
    let tmpOffset = mgi.ts?.getTime() - (state[lr].movieList.listSelection.currentMovieList?.hardStartTs?.getTime() || 0);
    tmpOffset = Math.max(tmpOffset, 0);
    tmpOffset = Math.min(tmpOffset, (state[lr].movieList.listSelection.currentMovieList?.fullDurationMsec || 0));
    const offsetMsec = tmpOffset + state[lr].playback.seek.min;
    await deps.seekMovie(lr, offsetMsec);
    updateMapPinPosition(lr, { lat: mgi.location.lat, lon: mgi.location.lon });
  };
  const alignLocationToLeftMovie = async() => {
    // 左側に合わせる
    const refMgi = state.left.geo.currentMgi;
    const mgis = state.right.movieList.listSelection.currentMovieList?.movies.map(movie => {
      return movie.movieGeoIndices;
    }).reduce((acc, e) => acc.concat(e), []);
    if (!refMgi || !mgis) return;

    const resultMgi = findClosestMgi(refMgi, mgis);
    await seekByMgi('right', resultMgi);
  };
  const alignLocationToRightMovie = async() => {
    // 右側に合わせる
    const refMgi = state.right.geo.currentMgi;
    const mgis = state.left.movieList.listSelection.currentMovieList?.movies.map(movie => {
      return movie.movieGeoIndices;
    }).reduce((acc, e) => acc.concat(e), []);
    if (!refMgi || !mgis) return;

    const resultMgi = findClosestMgi(refMgi, mgis);
    await seekByMgi('left', resultMgi);
  };
  const seekByMapClick = async(clickedLocation: Location) => {
    const playStateLeft = deps.getUIPlayState('left');
    const playStateRight = deps.getUIPlayState('right');
    // 両方停止する
    deps.pauseUI('left');
    deps.pauseUI('right');
    nextTick(async() => {
      // まず左を動かして、右はそちらの移動先に合わせる
      if (!state.left.movieList.listSelection.currentMovieList) return;

      const searchOpts = { featureIdPrefix: parseInt(state.left.movieList.listSelection.currentMovieList.id) };
      if (!deps.refExtremeMap.value) return;

      const mgi = deps.refExtremeMap.value.getClosestMgi(clickedLocation, searchOpts);
      await seekByMgi('left', mgi);
      await alignLocationToLeftMovie();
      nextTick(() => {
        // 元々再生中だった場合は、再生を再開
        if (playStateLeft === 'playing') {
          deps.playUI('left');
        }
        if (playStateRight === 'playing') {
          deps.playUI('right');
        }
      });
    });
  };
  const onMapItemClicked = async() => {
    // TODO
  };
  const onMapAllDeselected = async() => {
    // TODO
  };
  const startCreateMapElem = async(lr: LeftRight, vid: string) => {
    deps.pauseUI('left');
    deps.pauseUI('right');
    const currentMgi = state[lr].geo.currentMgi;
    if (!currentMgi) return;

    const screenshot = await deps.getScreenshotBlob(lr, vid);
    if (!(screenshot instanceof Blob)) return;

    state[lr].geo.mapElemInfo = {
      dataName: 'new',
      data: {
        lat: currentMgi.location.lat,
        lon: currentMgi.location.lon,
        kpUid: currentMgi.kpUid,
      },
      candidateImages: [screenshot],
    };
  };
  const createMapElem = async(lr: LeftRight, obj: MapElemInfo<GIComment>) => {
    if (!deps.parentGeoItemsMeta.map[obj.dataName]) {
      console.warn('tryCreateMapElem. Unknown resource type', obj.dataName);
      return;
    }
    const stateMgi = state[lr].geo.currentMgi;
    if (!stateMgi) return;

    // 保存ボタン押下時の位置を登録
    obj.data.location = { lat: parseFloat(stateMgi.location.lat), lon: parseFloat(stateMgi.location.lon) };
    const metaItem = deps.parentGeoItemsMeta.map[obj.dataName];
    try {
      const resource = await metaItem.manager.createResource(obj.data);
      if (deps.parentGeoItemsMeta.show[obj.dataName]) {
        metaItem.layerManager.addLayerItem(resource);
      }
      state[lr].geo.mapElemInfo = null;
      if (deps.refExtremeMap.value) {
        syncGeoItemWithParent(
          state.geoItemsMeta as GeoItemsMeta,
          deps.parentGeoItemsMeta,
          deps.refExtremeMap.value,
        );
      }
    } catch (e) {
      state[lr].geo.createMapElemFailed++;
    }
  };
  const clearMapElemInfo = (lr: LeftRight) => {
    state[lr].geo.mapElemInfo = null;
  };

  return reactive({
    mapElemIconPath,
    visibleGeoItemLayers,
    prepareMap,
    updateMap,
    updateMapPinPosition,
    alignLocationToLeftMovie,
    alignLocationToRightMovie,
    seekByMapClick,
    onMapItemClicked,
    onMapAllDeselected,
    startCreateMapElem,
    createMapElem,
    clearMapElemInfo,
  });
}
